import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Input } from "nlib/ui";
import Animations from "utils/Animations";
import BrowserEvents from "const/BrowserEvents";
import Button from "nlib/ui/Button";
import Colors from "const/Colors";
import ConfidencePercents from "nlib/common/ConfidencePercents";
import Constants from "const/Constants";
import EditDocumentConstants from "const/EditDocumentConstants";
import KeyNames from "const/KeyNames";
import Numbers from "utils/Numbers";
import Objects from "utils/Objects";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import classNames from "classnames";
import colorInterpolate from "color-interpolate";
import useDocumentContext from "hooks/useDocumentContext";

const DOUBLE_HEIGHT = 2;

const ContainerRefContext = createContext();

const preventEventPropagation = (event) => event.stopPropagation();

const FieldContainer = (props) => {
  const {
    id,
    label,
    value,
    invalid: propsInvalid,
    disabled: propsDisabled,
    component: Component = Input,
    selectable = true,
    placeholder,
    titleIcon,
    displayValue,
    confidenceFields,
    onClick,
    onChange,
    "data-tooltip": tooltip,
    "data-tooltip-style": tooltipStyle,
    ...restProps
  } = props;

  const containerRef = useContext(ContainerRefContext);

  const fieldRef = useRef();

  const {
    ocrMeta,
    documentFrozen,
    recogniseData: { confidence },
    localState: { activeField, hoveredField },
    setLocalState
  } = useDocumentContext();

  const { [id]: color = Colors.WHITE } = EditDocumentConstants.FIELD_COLORS;

  const edited = !confidence;

  const disabled = propsDisabled || documentFrozen;

  const interactive = !disabled && !edited && !!ocrMeta[id]?.boundingBox;

  const confidenceValues = !edited && confidenceFields
    ? confidenceFields?.map((field) => Objects.getPropertyByPath(confidence, field) || 0) : [];

  const confidenceValue = confidenceValues?.length ? Numbers.getAverageNumber(confidenceValues) : 0;

  const invalid = !disabled && propsInvalid;

  const fieldActive = activeField?.id === id;

  const fieldHovered = hoveredField?.id === id;

  const palette = useMemo(() => {
    return colorInterpolate([color, Colors.WHITE]);
  }, [color]);

  const style = useMemo(() => {
    return {
      borderLeft: `4px solid ${interactive ? color : "#fff"}`,
      backgroundColor: (fieldActive || fieldHovered) && interactive ? palette(fieldActive ? "0.85" : "0.95") : "#fff"
    };
  }, [palette, interactive, color, fieldActive, fieldHovered]);

  const handleFieldClick = useCallback((event) => {
    if (disabled) return;
    if (onClick) onClick(event);
    if (onChange) setLocalState({ activeField: { id, selectable } });
  }, [id, selectable, disabled, onChange, onClick, setLocalState]);

  const handleMouseEnter = useCallback(() => {
    setLocalState({ hoveredField: { id } });
  }, [setLocalState, id]);

  const handleMouseLeave = useCallback(() => {
    setLocalState({ hoveredField: null });
  }, [setLocalState]);

  const handleSaveClick = useCallback((event) => {
    event.stopPropagation();
    setLocalState({ activeField: null });
  }, [setLocalState]);

  const handleWindowKeyUp = useCallback(({ key }) => {
    if (key === KeyNames.ENTER) {
      setLocalState({ activeField: null });
    }
  }, [setLocalState]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (fieldHovered) {
      const { scrollTop: initialScroll } = containerRef.current;

      const elementRect = fieldRef.current.getBoundingClientRect();

      const containerRect = containerRef.current.getBoundingClientRect();

      const topMinimum = containerRect.top + elementRect.height;

      const topMaximum = containerRect.top + containerRect.height - (elementRect.height * DOUBLE_HEIGHT);

      if (elementRect.top < topMinimum) {
        const delta = topMinimum - elementRect.top;

        return Animations.animate((progress) => {
          containerRef.current.scrollTop = initialScroll - (delta * progress);
        });
      }
      if (elementRect.top > topMaximum) {
        const delta = elementRect.top - topMaximum;

        return Animations.animate((progress) => {
          containerRef.current.scrollTop = initialScroll + (delta * progress);
        });
      }
    }
  }, [fieldHovered, invalid, containerRef]);
  useEffect(() => {
    if (fieldActive) {
      window.addEventListener(BrowserEvents.KEY_UP, handleWindowKeyUp);
    }

    return () => {
      if (fieldActive) {
        window.removeEventListener(BrowserEvents.KEY_UP, handleWindowKeyUp);
      }
    };
  }, [fieldActive, handleWindowKeyUp]);

  return (
    <div
      ref={fieldRef}
      data-field={id}
      className={classNames(
        Css.fieldContainer,
        invalid && Css.invalid,
        invalid ? "invalidField" : "validField",
        fieldHovered && Css.hovered,
        fieldActive && Css.active,
        (!!onChange || !!onClick) && !disabled && Css.editable
      )}
      onClick={handleFieldClick}
      onMouseEnter={interactive ? handleMouseEnter : undefined}
      onMouseLeave={interactive ? handleMouseLeave : undefined}
      style={style}>
      <div className={Css.title}>
        <div
          className={Css.text}
          data-tooltip={tooltip}
          data-tooltip-style={tooltipStyle}>
          {invalid ? <Icons.WarningCircle /> : (titleIcon ? titleIcon : null)}
          <span>{label}</span>
        </div>
        {!!confidenceValue
          && !!confidenceValues?.length
          && value !== undefined
          && value !== null && (
          <ConfidencePercents
            className={Css.confidence}
            value={confidenceValue} />
        )}
      </div>
      <div className={Css.container}>
        {fieldActive && !disabled && !!onChange
          ? (
            <div className={Css.input} onClick={preventEventPropagation}>
              <Component
                autoFocus
                {...restProps}
                value={value}
                placeholder={placeholder}
                invalid={invalid}
                disabled={disabled}
                onChange={onChange} />
            </div>
          )
          : (
            <div className={Css.value}>
              {displayValue
                ? <div title={displayValue}>{displayValue}</div>
                : <div className={Css.placeholder}>{disabled ? Constants.EMPTY_PLACEHOLDER : placeholder}</div>}
            </div>
          )}
        {disabled
          ? <Button light disabled className={Css.iconButton}>
            <Icons.Lock />
          </Button>
          : (
            onChange
              ? (
                <Button
                  light
                  className={classNames(Css.iconButton, fieldActive && Css.saveButton)}
                  onClick={fieldActive ? handleSaveClick : undefined}>
                  <Icons.PencilSimple />
                </Button>
              )
              : (!onClick && (
                <Button
                  light disabled
                  className={Css.iconButton}>
                  <Icons.Lock />
                </Button>
              ))
          )}
      </div>
    </div>
  );
};

export { ContainerRefContext };

export default React.memo(FieldContainer);
